import { cn } from '../../utils/cn';

/**
 * Unified Page Header Component
 * 
 * Mobile-first header for all pages (except dashboard).
 * Provides consistent styling and behavior across the app.
 * 
 * @param {string|ReactNode} title - Page title (required)
 * @param {string|ReactNode} subtitle - Optional subtitle text or custom content
 * @param {function} onBack - Optional back button handler
 * @param {string|ReactNode} backLabel - Custom back button label/content (default: "←")
 * @param {ReactNode} actions - Optional action buttons/content
 * @param {ReactNode} children - Optional additional content (e.g., tabs)
 * @param {boolean} sticky - Make header sticky (default: true on mobile)
 * @param {string} maxWidth - Max width container (default: "1400px")
 * @param {string} className - Additional classes
 * 
 * Usage:
 * <PageHeader title="Page Title" subtitle="Optional subtitle" />
 * <PageHeader title="Page" onBack={() => navigate(-1)} actions={<Button>Action</Button>} />
 * <PageHeader title="Page" onBack={() => navigate(-1)}>
 *   <Tabs />
 * </PageHeader>
 */
export function PageHeader({
  title,
  subtitle,
  onBack,
  backLabel = '←',
  actions,
  children, // For tabs or additional content
  sticky = true,
  maxWidth = '1400px',
  className = '',
  ...props
}) {
  return (
    <header
      className={cn(
        'bg-white',
        // Only show border-b when there are no children (tabs)
        !children && 'border-b border-border',
        // Mobile: sticky below MobileHeader (which is fixed at top)
        // Desktop: sticky at top
        sticky && 'sticky top-0 md:top-0',
        // Mobile: z-40 (below MobileHeader's z-50), Desktop: z-100
        'z-40 md:z-[100]',
        // Mobile sticky header styling
        'mobile-sticky-header',
        className
      )}
      {...props}
    >
      <div
        className="mx-auto px-4 sm:px-6 md:px-10"
        style={{ maxWidth }}
      >
        {/* Main header content */}
        <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 ${
          children ? 'py-4 sm:py-5 md:py-6' : 'py-3 sm:py-4 md:py-5'
        }`}>
          {/* Left side: Back button + Icon + Title/Subtitle */}
          <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto min-w-0">
            {onBack && (
              <button
                onClick={onBack}
                className="text-xl sm:text-2xl text-text-secondary hover:text-text-primary transition-colors bg-transparent border-none cursor-pointer flex-shrink-0 touch-target min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                aria-label="Go back"
              >
                {backLabel}
              </button>
            )}
            <div className="min-w-0 flex-1">
                <h1 className="font-sans text-lg sm:text-xl md:text-2xl font-semibold text-text-primary truncate tracking-tighter leading-tight" style={{ letterSpacing: '-0.05em' }}>
                  {title}
                </h1>
                {subtitle && (
                  <div className="text-xs sm:text-sm text-text-secondary -mt-0.5">
                    {subtitle}
                  </div>
                )}
              </div>
            </div>

          {/* Right side: Actions */}
          {actions && (
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
              {actions}
            </div>
          )}
        </div>

        {/* Additional content (e.g., tabs) */}
        {children && (
          <div className="pt-0">
            {children}
          </div>
        )}
      </div>
    </header>
  );
}
